/**
 * QR codes, including the logo-in-the-middle kind.
 *
 * ONE renderer for both surfaces. The editor preview and the PNG/PDF export used
 * to call `QRCode.toDataURL` separately with their own options, which was fine
 * while a QR was just black squares — a logo plate is enough geometry (size,
 * padding, corner radius, backdrop, contain-fit) that two copies would drift and
 * the printed code would stop matching the design. Both now ask for the same
 * data URL and only choose the pixel size they need.
 *
 * WHY THE LOGO LIVES IN `element.src`: the backend lifts embedded images out of
 * a saved template by walking `canvasJson.elements` and looking at `el.src`, and
 * its orphan sweep counts the same field. A logo parked under any other key
 * would be stored as raw base64 inside the document, and — worse — once it was
 * an R2 URL the sweep would see nothing referring to that object and delete it.
 * So a QR element's `src` IS its logo.
 *
 * Error correction is raised to H whenever a logo is set: the mark covers
 * modules, and level H can lose ~30% of them and still scan.
 */
import QRCode from 'qrcode';
import { isTemplateMediaUrl, fetchMediaAsDataUrl } from './templateMedia';

/** Logo geometry, in PERCENT so it holds at any element size. */
export const QR_LOGO_DEFAULTS = {
    logoSize: 24, // plate side, % of the QR's own width
    logoPadding: 14, // quiet ring inside the plate, % of the plate
    logoRadius: 24, // plate corner radius, % of the plate
    logoBackground: '#ffffff',
};

const LEVELS = ['L', 'M', 'Q', 'H'];

export const hasQrLogo = (el) => typeof el?.src === 'string' && el.src.trim() !== '';

/** The level actually used: an explicit choice, else H with a logo and M without. */
export function qrErrorLevel(el) {
    const level = el?.styles?.errorCorrection;
    if (LEVELS.includes(level)) return level;
    return hasQrLogo(el) ? 'H' : 'M';
}

/** qrcode lib needs hex colours — map the CSS 'transparent' keyword to RGBA hex. */
export function qrColor(c, fallback) {
    if (!c) return fallback;
    return c === 'transparent' ? '#00000000' : c;
}

const pct = (value, fallback, max = 100) => {
    const n = Number(value ?? fallback);
    return Math.max(0, Math.min(max, Number.isFinite(n) ? n : fallback));
};

function decodeImage(src, { cors = false } = {}) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        if (cors) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load QR logo'));
        img.src = src;
    });
}

/**
 * Load the logo so it is safe to draw. The QR canvas is read back with
 * `toDataURL`, so a remote image loaded without CORS would taint it and throw —
 * same trick the exporter uses: try CORS, then pull our own media through the API.
 */
async function loadLogo(src) {
    if (/^(data:|blob:)/i.test(src)) return decodeImage(src);
    try {
        return await decodeImage(src, { cors: true });
    } catch (err) {
        if (!isTemplateMediaUrl(src)) throw err;
        return decodeImage(await fetchMediaAsDataUrl(src));
    }
}

function roundRectPath(ctx, x, y, w, h, r) {
    const radius = Math.max(0, Math.min(r, Math.min(w, h) / 2));
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
}

/**
 * The element as a PNG data URL, logo included.
 *
 * @param el     the QR element ({content, src, styles})
 * @param size   pixel side of the generated image (preview vs print)
 */
export async function renderQrDataUrl(el, { size = 512 } = {}) {
    const styles = el?.styles || {};
    const canvas = document.createElement('canvas');
    await QRCode.toCanvas(canvas, el?.content || ' ', {
        margin: styles.margin ?? 1,
        color: {
            dark: qrColor(styles.foreground, '#000000'),
            light: qrColor(styles.background, '#ffffff'),
        },
        width: size,
        errorCorrectionLevel: qrErrorLevel(el),
    });
    if (!hasQrLogo(el)) return canvas.toDataURL('image/png');

    let logo;
    try {
        logo = await loadLogo(el.src);
    } catch {
        // A logo that cannot be loaded must not cost the code itself.
        return canvas.toDataURL('image/png');
    }

    const ctx = canvas.getContext('2d');
    const side = canvas.width;
    // 40% is the practical ceiling: past that even level H stops scanning.
    const plate = (pct(styles.logoSize, QR_LOGO_DEFAULTS.logoSize, 40) / 100) * side;
    const padding = (pct(styles.logoPadding, QR_LOGO_DEFAULTS.logoPadding, 45) / 100) * plate;
    const radius = (pct(styles.logoRadius, QR_LOGO_DEFAULTS.logoRadius, 50) / 100) * plate;
    const px = (side - plate) / 2;
    const py = (side - plate) / 2;

    const backdrop = styles.logoBackground ?? QR_LOGO_DEFAULTS.logoBackground;
    if (backdrop && backdrop !== 'transparent') {
        ctx.fillStyle = backdrop;
        roundRectPath(ctx, px, py, plate, plate, radius);
        ctx.fill();
    }

    // Contain-fit inside the padded plate, so a wide logo keeps its proportions.
    const inner = Math.max(1, plate - padding * 2);
    const natural = Math.max(1, logo.naturalWidth || logo.width || 1);
    const naturalH = Math.max(1, logo.naturalHeight || logo.height || 1);
    const scale = Math.min(inner / natural, inner / naturalH);
    const dw = natural * scale;
    const dh = naturalH * scale;

    ctx.save();
    // Clip to the plate: a logo with square corners must not poke out of a
    // rounded backdrop.
    roundRectPath(ctx, px, py, plate, plate, radius);
    ctx.clip();
    ctx.drawImage(logo, px + (plate - dw) / 2, py + (plate - dh) / 2, dw, dh);
    ctx.restore();

    return canvas.toDataURL('image/png');
}
